import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import styles from './Dropdown.module.css';

interface DropdownItem {
  label: string | React.ReactNode;
  href?: string;
  onClick?: () => void;
  separator?: boolean;
  className?: string;
}

interface DropdownProps {
  trigger: React.ReactNode;
  items: DropdownItem[];
  placement?: 'bottom' | 'bottom-start' | 'bottom-end';
  className?: string;
}

export const Dropdown: React.FC<DropdownProps> = ({
  trigger,
  items,
  placement = 'bottom-end',
  className = '',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const calculatePosition = () => {
    if (!triggerRef.current || !dropdownRef.current) return;

    const triggerRect = triggerRef.current.getBoundingClientRect();
    const dropdownRect = dropdownRef.current.getBoundingClientRect();

    let top = triggerRect.bottom + 4;
    let left = triggerRect.left;

    switch (placement) {
      case 'bottom-start':
        left = triggerRect.left;
        break;
      case 'bottom-end':
        left = triggerRect.right - dropdownRect.width;
        break;
      case 'bottom':
      default:
        left = triggerRect.left + (triggerRect.width - dropdownRect.width) / 2;
        break;
    }

    // Keep dropdown within viewport
    if (left < 8) left = 8;
    if (left + dropdownRect.width > window.innerWidth - 8) {
      left = window.innerWidth - dropdownRect.width - 8;
    }

    setPosition({ top, left });
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (
      triggerRef.current &&
      !triggerRef.current.contains(event.target as Node) &&
      dropdownRef.current &&
      !dropdownRef.current.contains(event.target as Node)
    ) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      calculatePosition();
      document.addEventListener('mousedown', handleClickOutside);
      window.addEventListener('resize', calculatePosition);
      window.addEventListener('scroll', calculatePosition);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('resize', calculatePosition);
      window.removeEventListener('scroll', calculatePosition);
    };
  }, [isOpen]);

  const handleItemClick = (item: DropdownItem) => {
    if (item.onClick) {
      item.onClick();
    }
    setIsOpen(false);
  };

  return (
    <>
      <div
        ref={triggerRef}
        onClick={() => setIsOpen(!isOpen)}
        className={styles.trigger}
      >
        {trigger}
      </div>
      {isOpen && createPortal(
        <div
          ref={dropdownRef}
          className={`${styles.dropdown} ${className}`}
          style={{
            top: `${position.top}px`,
            left: `${position.left}px`,
          }}
        >
          <div className={styles.dropdownContent}>
            {items.map((item, index) => (
              <React.Fragment key={index}>
                {item.separator && index > 0 && <div className={styles.separator} />}
                {item.href ? (
                  <a
                    href={item.href}
                    className={`${styles.dropdownItem} ${item.className || ''}`}
                    onClick={() => handleItemClick(item)}
                  >
                    {item.label}
                  </a>
                ) : (
                  <button
                    className={`${styles.dropdownItem} ${item.className || ''}`}
                    onClick={() => handleItemClick(item)}
                  >
                    {item.label}
                  </button>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>,
        document.body
      )}
    </>
  );
};